<!-- ============================================================================
  PlateX: Number plates under £200 (dev2 Register restyle).
  Hero → formats → sticky toolbar + live list → questions.
============================================================================= -->

<!-- ============================ HERO ============================ -->
<section class="hero">
  <div class="wrap hero-grid">
    <div class="hero-lead">
      <span class="eyebrow">Budget plates · Under £200</span>
      <h1>Number plates under £200.</h1>
    </div>
    <div class="hero-figure">
      <div class="visual"><img [src]="heroImage" [alt]="'Fiat 500 wearing the registration ' + (heroPlate?.registration || 'SUB 200')" /></div>
      <p class="figcap" *ngIf="heroPlate">Fiat 500 wearing <a [routerLink]="plateLink(heroPlate.registration)">{{ heroPlate.registration }}</a>, on sale now from {{ pounds(heroPlate.price) }}.</p>
    </div>
    <div class="hero-rest">
      <p>A private plate does not have to cost a fortune. Every registration here is on the market now for under £200, price-compared across UK dealers, with fees included in the price you see.</p>
      <div class="hero-cta">
        <a class="btn btn--green" href="#plates" (click)="jumpTo('plates', $event)">Browse the plates</a>
        <a class="btn btn--ghost" href="#formats" (click)="jumpTo('formats', $event)">Which format suits my car?</a>
      </div>
    </div>
  </div>
  <div class="wrap">
    <div class="hero-stats">
      <div class="s"><div class="n">{{ loading ? '…' : num(stats.total) }}</div><div class="l">Plates under £200 · on the market now</div></div>
      <div class="s"><div class="n">{{ loading ? '…' : 'from ' + pounds(stats.from) }}</div><div class="l">Cheapest plate · fees included</div></div>
      <div class="s"><div class="n">{{ loading ? '…' : num(stats.under150) }}</div><div class="l">Under £150</div></div>
      <div class="s"><div class="n">{{ loading ? '…' : num(stats.dateless) }}</div><div class="l">Dateless · fit any vehicle</div></div>
    </div>
  </div>
</section>

<!-- ============================ FORMATS ============================ -->
<section class="section section--paper formats jump-target" id="formats">
  <div class="wrap">
    <div class="sec-head">
      <span class="eyebrow">What £200 buys</span>
      <h2 class="h-sec">Four formats. Pick the one your car can wear.</h2>
      <p class="lead">Age identifiers can make a vehicle look older, never newer. Dateless plates carry no year at all, which is why they are the most popular choice at this price.</p>
    </div>
    <div class="fmt-grid">
      <button type="button" class="fmt-card" *ngFor="let f of formats" [class.on]="format === f.key" (click)="setFormat(f.key, true)">
        <span class="plate-y"><span class="ch">{{ f.example }}</span></span>
        <span class="t">{{ f.label }}</span>
        <span class="d">{{ f.rule }}</span>
        <span class="m" *ngIf="!loading">
          <b>{{ num(formatTotals[f.key] || 0) }}</b> for sale<ng-container *ngIf="formatFrom[f.key] !== undefined"> · from {{ pounds(formatFrom[f.key]) }}</ng-container>
          <span class="arw">→</span>
        </span>
      </button>
    </div>
  </div>
</section>

<!-- ============================ THE PLATES ============================ -->
<div id="plates" class="jump-target">
  <div class="reg-heading">
    <div class="wrap">
      <span class="eyebrow">The plates</span>
      <h2 class="h-sec">Every plate under £200.</h2>
    </div>
  </div>

  <div class="toolbar">
    <div class="wrap">
      <div class="tb-item">
        <span class="tb-lbl">Format</span>
        <div class="tb-group">
          <button type="button" [class.active]="format === 'all'" (click)="setFormat('all')">All</button>
          <button type="button" *ngFor="let f of formats" [class.active]="format === f.key" [disabled]="!loading && !formatCounts[f.key]" (click)="setFormat(f.key)">
            {{ f.label }} <span class="c" *ngIf="!loading">{{ num(formatCounts[f.key]) }}</span>
          </button>
        </div>
      </div>
      <div class="tb-item">
        <span class="tb-lbl">Price</span>
        <div class="tb-group">
          <button type="button" *ngFor="let b of bands" [class.active]="band === b.key" (click)="setBand(b.key)">{{ b.label }}</button>
        </div>
      </div>
      <div class="tb-item tb-search">
        <span class="tb-lbl">Contains</span>
        <div class="search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input #termEl type="text" maxlength="8" placeholder="Letters or numbers, e.g. your initials" autocomplete="off" spellcheck="false"
                 [value]="term" (input)="setTerm(termEl.value); termEl.value = term" />
        </div>
      </div>
      <div class="tb-item">
        <span class="tb-lbl">Sort</span>
        <select class="tb-select" [value]="sort" (change)="setSort($any($event.target).value)">
          <option value="price-asc">Price, low to high</option>
          <option value="price-desc">Price, high to low</option>
          <option value="az">A to Z</option>
          <option value="za">Z to A</option>
        </select>
      </div>
      <span class="tb-count">
        <span *ngIf="loading">Loading plates…</span>
        <span *ngIf="!loading"><b>{{ num(shownCount) }}</b> plate{{ shownCount === 1 ? '' : 's' }}</span>
        <a class="tb-clear" *ngIf="filtered" (click)="clearFilters()" role="button" tabindex="0" (keydown.enter)="clearFilters()">Clear</a>
      </span>
    </div>
  </div>

  <section class="register">
    <div class="wrap">
      <p class="empty" *ngIf="!loading && shownCount === 0">
        No plates under £200 match these filters. <a (click)="clearFilters()" role="button" tabindex="0" (keydown.enter)="clearFilters()">Clear the filters</a> to see all {{ num(stats.total) }}.
      </p>
      <div [hidden]="!loading && shownCount === 0">
        <pla-search-result-table
          [searching]="loading"
          [results]="view"
          [resultChange]="viewChange"
          [customHeaderContent]="true"
          [customHeader]="noHeader"
          [showFilter]="false"
          [showSort]="false"
          [showToggle]="false"
          [showFullSeries]="true"
          [sortOnStart]="false"
          [paginationPageSize]="50">
        </pla-search-result-table>
      </div>
    </div>
  </section>
</div>
<ng-template #noHeader></ng-template>

<!-- ============================ QUESTIONS ============================ -->
<section class="section section--paper faq-sec jump-target" id="questions" *ngIf="faq?.length">
  <div class="wrap">
    <div class="sec-head">
      <span class="eyebrow">Questions</span>
      <h2 class="h-sec">Buying a plate on a budget.</h2>
    </div>
    <div class="faq-wrap">
      <pla-faq [faq]="faq"></pla-faq>
    </div>
  </div>
</section>
